import { useEffect, useState } from "react";
import { db, functions } from "@/firebase/config";
import { collection, limit, onSnapshot, orderBy, query, doc, where } from "firebase/firestore";
import { Loader2, Receipt, Calendar, Filter, ArrowLeft, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { httpsCallable } from "firebase/functions";

// This component now relies on parent for auth + schoolID and only fetches transactions.
interface Transaction {
  transactionId: string;
  amount: number;
  type: string;
  status: string;
  phoneNumber: string;
  transactionDate: any;
  provider: string;
  mpesaDetails?: { receiptNumber?: string; accountReference?: string; };
  accountReference?: string;
  createdAt: any;
}

interface GuardianTransactionsProps {
  schoolID: string | null;
  accountReference?: string;
  onBack: () => void;
}

const GuardianTransactions = ({ schoolID, accountReference, onBack }: GuardianTransactionsProps) => {
  const { toast } = useToast();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolID) return;
    setLoadingTransactions(true);
    
    let qTx;
    if (accountReference) {
      qTx = query(
        collection(db, `schools/${schoolID}/transactions/mpesa/records`), 
        where("accountReference", "==", accountReference),
        orderBy("createdAt", "desc"), 
        limit(100)
      );
    } else {
      qTx = query(
        collection(db, `schools/${schoolID}/transactions/mpesa/records`), 
        orderBy("createdAt", "desc"), 
        limit(100)
      );
    }

    const unsub = onSnapshot(qTx, (snap) => {
      const list: Transaction[] = snap.docs.map(d => ({ transactionId: d.id, ...(d.data() as any) })) as Transaction[];
      setTransactions(list);
      console.log("[GuardianTransactions] Snapshot count:", snap.size, list.slice(0,3).map(x => ({ id: x.transactionId, status: x.status, amount: x.amount, receipt: x.mpesaDetails?.receiptNumber, reference: x.mpesaDetails?.accountReference || x.accountReference })));
      setLoadingTransactions(false);
    }, (err) => {
      console.error("[GuardianTransactions] onSnapshot error:", err);
      toast({ title: "Error", description: "Failed to load transactions", variant: "destructive" });
      setLoadingTransactions(false);
    });
    return () => unsub();
  }, [schoolID, toast]);

  const handleCheckStatus = async (checkoutRequestId: string, retryCount = 0) => {
    if (!schoolID) return;
    
    setCheckingStatus(checkoutRequestId);
    let shouldClearStatus = true;
    
    try {
      const queryMpesaStatus = httpsCallable(functions, "queryMpesaStkStatus");
      const result = await queryMpesaStatus({
        schoolId: schoolID,
        checkoutRequestId: checkoutRequestId,
      });
      
      const response = result.data as any;
      console.log("[GuardianTransactions] Status check result:", response);
      
      // Check Firestore for updated status
      const txDocRef = doc(db, `schools/${schoolID}/transactions/mpesa/records/${checkoutRequestId}`);
      const snap = await import("firebase/firestore").then(m => m.getDoc(txDocRef));
      
      if (snap.exists()) {
        const data = snap.data();
        
        if (data.status === "completed") {
          toast({
            title: "✅ Payment Confirmed",
            description: `Payment of KES ${data.amount?.toLocaleString()} was successful!`,
            duration: 5000,
          });
        } else if (data.status === "failed") {
          toast({
            title: "❌ Payment Failed",
            description: response.resultDesc || data.resultDesc || "The payment could not be completed.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          toast({
            title: "📊 Still Pending",
            description: response.resultDesc || "Payment is still being processed.",
            duration: 5000,
          });
        }
      } else {
        toast({
          title: "📊 Status Retrieved",
          description: response.resultDesc || "Status queried from M-Pesa",
          duration: 5000,
        });
      }
    } catch (err: any) {
      console.error("[GuardianTransactions] Status check error:", err);
      
      // Handle 429 rate limit error with automatic retry
      if (err.message?.includes("429") && retryCount < 3) {
        const waitTime = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
        console.log(`[GuardianTransactions] Rate limited, retrying in ${waitTime}ms (attempt ${retryCount + 1}/3)`);
        
        toast({
          title: "⏳ Rate Limited",
          description: `Retrying automatically in ${waitTime / 1000} second${waitTime > 1000 ? 's' : ''}...`,
          duration: waitTime,
        });
        
        shouldClearStatus = false; // Don't clear status, we're retrying
        
        setTimeout(() => {
          handleCheckStatus(checkoutRequestId, retryCount + 1);
        }, waitTime);
        return;
      }
      
      // Handle specific M-Pesa errors gracefully
      if (err.message?.includes("insufficient") || err.message?.includes("balance")) {
        toast({
          title: "⚠️ Unable to Verify",
          description: "This transaction cannot be verified automatically. Please check your M-Pesa messages or wait for the transaction to complete.",
          duration: 8000,
        });
      } else if (err.message?.includes("timeout") || err.message?.includes("ETIMEDOUT")) {
        toast({
          title: "⏱️ Request Timeout",
          description: "The verification request timed out. The payment may still be processing.",
          duration: 6000,
        });
      } else {
        toast({
          title: "Error Checking Status",
          description: err.message || "Failed to retrieve payment status",
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      if (shouldClearStatus) {
        setCheckingStatus(null);
      }
    }
  };

  const filtered = transactions.filter(tx => filterStatus === "all" ? true : tx.status === filterStatus);
  const formatDate = (date: any) => { try { const d = date?.toDate ? date.toDate() : new Date(date); return d.toLocaleDateString()+" "+d.toLocaleTimeString(); } catch { return "N/A"; } };
  const getStatusColor = (status: string) => { switch (status) { case "completed": return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"; case "pending": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"; case "failed": return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"; default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"; } };
  const getPaymentMode = (provider: string) => {
    if (provider?.toLowerCase() === "mpesa") return "M-Pesa";
    return provider || "Unknown";
  };

  if (!schoolID) return (
    <div className="p-6 text-sm text-red-600 dark:text-red-400">School context missing.</div>
  );

  return (
    <div className="max-w-6xl mx-auto">
              <div className="flex items-center mb-6">
                <button onClick={onBack} className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 mr-4">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </button>
                <div className="flex items-center gap-2">
                  <Receipt className="h-6 w-6 text-primary" />
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Transaction History</h1>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-4 w-4 text-gray-500" />
                <select aria-label="Filter transactions by status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                  <option value="all">All</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {loadingTransactions ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                  <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No transactions found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filtered.map(tx => (
                          <tr key={tx.transactionId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" />{formatDate(tx.transactionDate || tx.createdAt)}</div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300 break-all max-w-xs">{tx.mpesaDetails?.accountReference || tx.accountReference || "N/A"}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">KES {tx.amount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{getPaymentMode(tx.provider)}</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400 break-all max-w-[120px]">{tx.mpesaDetails?.receiptNumber || "-"}</td>
                            <td className="px-4 py-3"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tx.status)}`}>{tx.status}</span></td>
                            <td className="px-4 py-3">
                              {tx.status === "pending" && (
                                <button
                                  onClick={() => handleCheckStatus(tx.transactionId)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                >
                                  {checkingStatus === tx.transactionId ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      Checking...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="h-3 w-3" />
                                      Check Status
                                    </>
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
    </div>
  );
};

export default GuardianTransactions;
